import { FILTER_CONFIG_NAMES } from './DataConstants';
import { isBuyerOrInternalTech, isInternalTech } from './AuthUtils';
import { fetchWithDifferntSearchName } from './DataAccessUtils';

// Shared sort options for the buyer and supplier asset index pages.
// `order: 'ascend'` maps to `asc` in restCrudThunksGenerator; anything else maps to `desc`.
//
// NOTE: These sorts are only honored on the OpenSearch read path (searchWithPostMethod),
// which is gated behind the `assets-openwsearch` flag — so only pass these to ReduxDataTable
// when that flag is enabled. The backend (AssetOpenSearchHelper.getSortQueries) only supports
// `daysInUse` (asc/desc) and `name` ascending; name descending is intentionally omitted because
// the backend ignores order for name and always returns A–Z.
export const ASSET_SORTERS = [
	{ label: 'Name (A–Z)', selectVal: 'name_asc', value: { sort_by: 'name', order: 'ascend' } },
	{
		// Oldest effective age = most time in use = highest daysInUse.
		label: 'Age (oldest)',
		selectVal: 'daysInUse_desc',
		value: { sort_by: 'daysInUse', order: 'desc' },
	},
	{
		// Newest effective age = least time in use = lowest daysInUse.
		label: 'Age (newest)',
		selectVal: 'daysInUse_asc',
		value: { sort_by: 'daysInUse', order: 'ascend' },
	},
];

// Strip handleChange so ReduxDataTable uses its own internal handler
// which triggers both state update and data refetch via updateFiltersEntities.
const stripHandleChange = (config) => {
	if (!config) return config;
	const { handleChange, ...rest } = config;
	return rest;
};

export const getAssetFilterConfig = ({
	filters,
	allowArea = false,
	currentUser = null,
	removeAssetNumberToQRCodeAssociation = false,
	replacementIndexEnabled = false,
}: {
	filters: any;
	allowArea?: boolean;
	currentUser?: any;
	removeAssetNumberToQRCodeAssociation?: boolean;
	replacementIndexEnabled?: boolean;
}) => {
	return [
		filters[FILTER_CONFIG_NAMES.SERIAL_NUMBER],
		filters[FILTER_CONFIG_NAMES.LOCATION],
		...(allowArea ? [filters[FILTER_CONFIG_NAMES.AREA]] : []),
		...(isBuyerOrInternalTech(currentUser) ? [filters[FILTER_CONFIG_NAMES.ASSET_TYPE]] : []),
		filters[FILTER_CONFIG_NAMES.DOWNTIME_STATUS],
		filters[FILTER_CONFIG_NAMES.ASSET_HAS_WARRANTY],
		filters[FILTER_CONFIG_NAMES.TOP_LEVEL_ONLY],
		...(removeAssetNumberToQRCodeAssociation ? [filters[FILTER_CONFIG_NAMES.ASSET_TAGGED]] : []),
		...(replacementIndexEnabled ? [filters[FILTER_CONFIG_NAMES.REPLACEMENT_INDEX]] : []),
		filters[FILTER_CONFIG_NAMES.IS_ACTIVE] && {
			...filters[FILTER_CONFIG_NAMES.IS_ACTIVE],
			showOutsideDropdown: true,
			info: undefined,
		},
		filters[FILTER_CONFIG_NAMES.INSTALL_AT],
	]
		.filter(Boolean)
		.map(stripHandleChange);
};

/**
 * Filters for the Refrigerant Tracking ▸ Assets list.
 *
 * Deliberately a short list rather than the full asset config, and IS_ACTIVE is deliberately absent:
 * active/inactive is driven by the `inactive` bucket chip, and a second control writing the same
 * `isActive` key would let the two disagree about which appliances the page is showing.
 */
export const getRefrigerantAssetFilterConfig = ({
	filters,
	currentUser = null,
}: {
	filters: any;
	currentUser?: any;
}) => {
	return [
		filters[FILTER_CONFIG_NAMES.LOCATION],
		...(isBuyerOrInternalTech(currentUser) ? [filters[FILTER_CONFIG_NAMES.ASSET_TYPE]] : []),
		filters[FILTER_CONFIG_NAMES.SERIAL_NUMBER],
	]
		.filter(Boolean)
		.map(stripHandleChange);
};

/**
 * Filters for the Refrigerant Tracking ▸ Event Log.
 *
 * Asset, Location and Asset Type — the three ways somebody narrows a whole tenant's servicing record
 * down to the thing they are actually auditing. All three are keys on the ledger's own list endpoint
 * (`assetIds` / `locationIds` / `assetTypeIds`), resolved through the joined appliance row: the server
 * SILENTLY IGNORES filter keys it does not recognise, so a filter offered here without its backend
 * case would render as selected while returning the unfiltered ledger.
 *
 * Deliberately no event-type filter: the pipeline chips above the table already own `eventTypes`, and
 * a second control writing the same key would let the two disagree about what the page is showing.
 *
 * `searchAssetsLite`, when supplied, redirects the ASSET filter's type-to-search onto the
 * OpenSearch-backed `POST /asset/assets/search/lite` instead of the shared `readLite`, which is the
 * raw-SQL listing path. Overridden HERE rather than on `useFilters`' binding so the other pages that
 * offer an Asset filter (PM schedules, work orders, invoices, quotes) keep the search they already
 * have. `AdvancedFilters` calls `fetch({ name: searchText }, ...)`, so the override is wrapped the same
 * way the shared binding is — see `fetchWithDifferntSearchName`.
 */
export const getRefrigerantEventFilterConfig = ({
	filters,
	currentUser = null,
	searchAssetsLite = null,
}: {
	filters: any;
	currentUser?: any;
	searchAssetsLite?: any;
}) => {
	const assetFilter = filters[FILTER_CONFIG_NAMES.ASSET];
	return [
		assetFilter && searchAssetsLite
			? { ...assetFilter, fetch: fetchWithDifferntSearchName(searchAssetsLite, 'search') }
			: assetFilter,
		filters[FILTER_CONFIG_NAMES.LOCATION],
		...(isBuyerOrInternalTech(currentUser) ? [filters[FILTER_CONFIG_NAMES.ASSET_TYPE]] : []),
	]
		.filter(Boolean)
		.map(stripHandleChange);
};

export const getSupplierAssetFilterConfig = ({
	filters,
	allowArea = false,
	currentUser = null,
	showDepreciationClass = false,
	removeAssetNumberToQRCodeAssociation = false,
}: {
	filters: any;
	allowArea?: boolean;
	currentUser?: any;
	showDepreciationClass?: boolean;
	removeAssetNumberToQRCodeAssociation?: boolean;
}) => {
	return [
		filters[FILTER_CONFIG_NAMES.SERIAL_NUMBER],
		filters[FILTER_CONFIG_NAMES.LOCATION],
		...(allowArea ? [filters[FILTER_CONFIG_NAMES.AREA]] : []),
		filters[FILTER_CONFIG_NAMES.ASSET_TYPE],
		filters[FILTER_CONFIG_NAMES.MANUFACTURER],
		...(isInternalTech(currentUser) ? [filters[FILTER_CONFIG_NAMES.VENDOR]] : []),
		...(showDepreciationClass ? [filters[FILTER_CONFIG_NAMES.DEPRECIATION_CLASS]] : []),
		filters[FILTER_CONFIG_NAMES.ADDITIONAL_FIELD],
		filters[FILTER_CONFIG_NAMES.INSTALL_AT],
		filters[FILTER_CONFIG_NAMES.ASSET_HAS_WARRANTY],
		...(removeAssetNumberToQRCodeAssociation ? [filters[FILTER_CONFIG_NAMES.ASSET_TAGGED]] : []),
		filters[FILTER_CONFIG_NAMES.DOWNTIME_STATUS],
		filters[FILTER_CONFIG_NAMES.IS_ACTIVE] && {
			...filters[FILTER_CONFIG_NAMES.IS_ACTIVE],
			showOutsideDropdown: true,
			info: undefined,
		},
	]
		.filter(Boolean)
		.map(stripHandleChange);
};
